import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  setDoc,
  doc
} from 'firebase/firestore';
import CondominiumCard from './CondominiumCard';
import SEO from './SEO';
import ScrollToTop from './ScrollToTop';
import BreadcrumbNav from './seo/BreadcrumbNav';
import PreLoader from './PreLoader';
import PropertyGridSkeleton from './PropertyGridSkeleton';
import { showNotification } from './Notification';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useMapSettings } from '../hooks/useMapSettings';
import { logCondominiumDelete, logCondominiumUpdate, buildChangeDiff } from '../utils/logger';
import MapErrorBoundary from './MapErrorBoundary';
import {
  condoInitialFilters,
  toJsDate,
  filterCondominiums,
  sortCondominiums,
  condosToMapProperties
} from '../utils/condominiumListUtils';
import {
  fetchCondominiumsCatalog,
  fetchPropertiesCatalog,
  buildCondominiumPropertyCounts,
  isPublicListableProperty,
  invalidateCondominiumsCatalog,
} from '../services/propertiesCatalog';
import { CATALOG_NS, getCachedCatalog } from '../services/catalogCache';

const Select = lazy(() => import('react-select'));
const PropertyMap = lazy(() => import('./PropertyMap'));
const MapboxPropertyMap = lazy(() => import('./MapboxPropertyMap'));
const QuickEditModal = lazy(() => import('./QuickEditModal'));

const SelectFallback = () => (
  <div className="h-[46px] w-full rounded-lg border border-[#cfd4dd] bg-gray-50 animate-pulse" aria-hidden="true" />
);

const MapViewFallback = () => (
  <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
    <span className="text-gray-500">Carregando mapa...</span>
  </div>
);

function isActivePublic(docData) {
  if (docData.isInactive === true) return false;
  if (docData.isAvailable === false) return false;
  return true;
}

const customStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? 'var(--primary-color, #000000)' : '#cfd4dd',
    borderRadius: '0.5rem',
    minHeight: '46px',
    height: '46px',
    boxShadow: 'none',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'var(--primary-color, #000000)'
    },
    '&:focus, &:focus-within': {
      border: '2px solid var(--primary-color, #000000)',
      boxShadow: 'none'
    }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--primary-color, #3B82F6)'
      : state.isFocused
      ? '#f3f4f6'
      : 'white',
    color: state.isSelected
      ? 'var(--secondary-color, #ffffff)'
      : '#111827',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: state.isSelected
        ? 'var(--primary-color, #3B82F6)'
        : '#e5e7eb',
      color: state.isSelected
        ? 'var(--secondary-color, #ffffff)'
        : '#111827'
    }
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  singleValue: (base, { selectProps }) => ({
    ...base,
    color: selectProps.hasValue ? '#111827' : '#6B7280'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6b7280'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 12px'
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    outline: 'none',
    border: 'none',
    boxShadow: 'none',
    '& input': {
      outline: 'none !important',
      boxShadow: 'none !important',
      borderColor: 'transparent !important'
    },
    '& input:focus': {
      outline: 'none !important',
      boxShadow: 'none !important',
      borderColor: 'transparent !important'
    }
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#e2e8f0',
    borderRadius: '0.375rem'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#1f2937'
  }),
  multiValueRemove: (base) => ({
    ...base,
    ':hover': {
      backgroundColor: '#cbd5e1',
      color: '#1f2937'
    }
  }),
  menu: (base) => ({
    ...base,
    cursor: 'pointer',
    zIndex: 9999
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  dropdownIndicator: (base) => ({
    ...base,
    cursor: 'pointer'
  })
};

const typeOptions = [
  { value: '', label: 'Tipo Imóvel' },
  { value: 'house', label: 'Casa' },
  { value: 'apartment', label: 'Apartamento' },
  { value: 'loft', label: 'Loft' },
  { value: 'warehouse', label: 'Galpão' },
  { value: 'store', label: 'Loja' },
  { value: 'building', label: 'Prédio' },
  { value: 'office', label: 'Sala' },
  { value: 'land', label: 'Terreno' },
  { value: 'farm', label: 'Sítio' },
  { value: 'countryHouse', label: 'Chácara' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'rural', label: 'Rural' }
];

const purposeOptions = [
  { value: '', label: 'Finalidade' },
  { value: 'sale', label: 'Venda' },
  { value: 'rent', label: 'Aluguel' }
];

const priceRangeOptions = [
  { value: '-', label: 'Qualquer valor' },
  { value: '0-2000', label: 'Até R$ 2.000/m²' },
  { value: '2000-4000', label: 'De R$ 2.000 até R$ 4.000/m²' },
  { value: '4000-6000', label: 'De R$ 4.000 até R$ 6.000/m²' },
  { value: '6000-8000', label: 'De R$ 6.000 até R$ 8.000/m²' },
  { value: '8000-10000', label: 'De R$ 8.000 até R$ 10.000/m²' },
  { value: '10000-15000', label: 'De R$ 10.000 até R$ 15.000/m²' },
  { value: '15000-', label: 'Acima de R$ 15.000/m²' }
];

const areaRangeOptions = [
  { value: '-', label: 'Qualquer área' },
  { value: '0-500', label: 'Até 500m²' },
  { value: '500-1000', label: '500m² até 1.000m²' },
  { value: '1000-5000', label: '1.000m² até 5.000m²' },
  { value: '5000-10000', label: '5.000m² até 10.000m²' },
  { value: '10000-50000', label: '10.000m² até 50.000m²' },
  { value: '50000-', label: 'A partir de 50.000m²' }
];

const unitsRangeOptions = [
  { value: '-', label: 'Qualquer' },
  { value: '0-49', label: 'Menos de 50' },
  { value: '50-100', label: '50 a 100' },
  { value: '100-200', label: '100 a 200' },
  { value: '200-', label: 'Mais de 200' }
];

const displayStatusOptions = [
  { value: '', label: 'Qualquer' },
  { value: 'reserved', label: 'Reservado' },
  { value: 'featured', label: 'Em destaque' },
  { value: 'launch', label: 'Lançamento' },
  { value: 'preLaunch', label: 'Pré lançamento' }
];

const orderByOptions = [
  { value: 'price_asc', label: 'Menor preço' },
  { value: 'price_desc', label: 'Maior preço' },
  { value: 'newest', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigos' },
  { value: 'area_asc', label: 'Menor área' },
  { value: 'area_desc', label: 'Maior área' },
  { value: 'units_asc', label: 'Menos unidades' },
  { value: 'units_desc', label: 'Mais unidades' }
];

export default function CondominiumsPublicListView() {
  const { companyName } = useCompanySettings();
  const { user } = useAuth();
  const { canEditProperties, canDeleteProperties, canCreateProperties, canAccessAdmin } =
    usePermissions();
  const { mapSettings } = useMapSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [propertyCounts, setPropertyCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isPreLoading, setIsPreLoading] = useState(() => {
    try {
      return !getCachedCatalog(CATALOG_NS.CONDOMINIUMS, 'public:all');
    } catch {
      return true;
    }
  });
  const [filters, setFilters] = useState(() => ({
    ...condoInitialFilters,
    location: (searchParams.get('location') || '').trim(),
    neighborhood: (searchParams.get('neighborhood') || '').trim()
  }));
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [viewType, setViewType] = useState('grid');
  const [displayedCondos, setDisplayedCondos] = useState([]);
  const [hasMoreCondos, setHasMoreCondos] = useState(true);
  const [userFavorites, setUserFavorites] = useState([]);
  const [shareModal, setShareModal] = useState({ isOpen: false, condo: null });
  const [quickEditModal, setQuickEditModal] = useState({ isOpen: false, condo: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, condo: null });
  const [selectedCondoId, setSelectedCondoId] = useState(null);
  const [hoveredCondoId, setHoveredCondoId] = useState(null);
  const [visibleCondos, setVisibleCondos] = useState([]);
  const [mapKey, setMapKey] = useState(0);
  const [availableNeighborhoods, setAvailableNeighborhoods] = useState([]);

  const loadMoreRef = useRef(null);

  const availableCities = useMemo(() => {
    return [...new Set(items.map((c) => c.city).filter(Boolean))].sort();
  }, [items]);

  const cityOptions = useMemo(
    () => availableCities.map((city) => ({ value: city, label: city })),
    [availableCities]
  );

  const allFilteredCondos = useMemo(() => {
    const filtered = filterCondominiums(items, filters);
    return sortCondominiums(filtered, filters.orderBy);
  }, [items, filters]);

  const totalCondos = allFilteredCondos.length;

  const mapProperties = useMemo(
    () => condosToMapProperties(allFilteredCondos, propertyCounts),
    [allFilteredCondos, propertyCounts]
  );

  const loadUserFavorites = useCallback(async () => {
    if (!user) {
      const localFavorites = JSON.parse(localStorage.getItem('localCondominiumFavorites') || '[]');
      setUserFavorites(localFavorites);
      return;
    }
    try {
      const db = getFirestore();
      const favoritesSnapshot = await getDocs(
        query(collection(db, 'favorites'), where('userId', '==', user.uid))
      );
      const favoriteIds = favoritesSnapshot.docs
        .map((d) => d.data().condominiumId)
        .filter(Boolean);
      setUserFavorites(favoriteIds);
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    let idleId = null;
    let timeoutId = null;

    const scheduleIdle = (fn) => {
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(fn, { timeout: 2500 });
      } else {
        timeoutId = setTimeout(fn, 50);
      }
    };

    (async () => {
      setLoading(true);
      try {
        const condos = await fetchCondominiumsCatalog();
        if (cancelled) return;
        const list = condos
          .map((d) => ({ ...d, createdAt: toJsDate(d.createdAt) }))
          .filter(isActivePublic);
        setItems(list);
        setLoading(false);

        // Contagens de imóveis: não bloqueia a listagem
        scheduleIdle(async () => {
          if (cancelled) return;
          try {
            const properties = await fetchPropertiesCatalog({ scope: 'public' });
            if (cancelled) return;
            setPropertyCounts(
              buildCondominiumPropertyCounts(properties.filter(isPublicListableProperty))
            );
          } catch (countsError) {
            console.error('Erro ao carregar contagens de imóveis:', countsError);
          }
        });
      } catch (e) {
        console.error('Erro ao carregar condomínios:', e);
        if (!cancelled) {
          setItems([]);
          setPropertyCounts({});
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    loadUserFavorites();
  }, [loadUserFavorites]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [searchParams]);

  useEffect(() => {
    if (filters.location) {
      const neighborhoods = [
        ...new Set(
          items
            .filter((c) => String(c.city || '').trim() === filters.location)
            .map((c) => c.neighborhood)
            .filter(Boolean)
        )
      ].sort();
      setAvailableNeighborhoods(neighborhoods);
    } else {
      setAvailableNeighborhoods([]);
    }
  }, [filters.location, items]);

  useEffect(() => {
    setDisplayedCondos(allFilteredCondos.slice(0, 8));
    setHasMoreCondos(allFilteredCondos.length > 8);
  }, [allFilteredCondos]);

  useEffect(() => {
    if (!loading) {
      setIsPreLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    if (viewType === 'map') {
      setVisibleCondos(allFilteredCondos);
      setMapKey((k) => k + 1);
    }
  }, [viewType, allFilteredCondos]);

  useEffect(() => {
    if (!mapSettings.enabled && viewType === 'map') {
      setViewType('grid');
    }
  }, [mapSettings.enabled, viewType]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640 && (viewType === 'list' || viewType === 'map')) {
        setViewType('grid');
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [viewType]);

  useEffect(() => {
    if (viewType === 'map' || !loadMoreRef.current || loading || !hasMoreCondos) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loading && hasMoreCondos) {
          const currentLength = displayedCondos.length;
          const nextCondos = allFilteredCondos.slice(currentLength, currentLength + 8);
          if (nextCondos.length > 0) {
            setDisplayedCondos((prev) => [...prev, ...nextCondos]);
            setHasMoreCondos(allFilteredCondos.length > currentLength + 8);
          } else {
            setHasMoreCondos(false);
          }
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    const loadMoreNode = loadMoreRef.current;
    observer.observe(loadMoreNode);
    return () => {
      if (loadMoreNode) {
        observer.unobserve(loadMoreNode);
      }
    };
  }, [viewType, loading, hasMoreCondos, displayedCondos.length, allFilteredCondos]);

  const handlePreLoadingComplete = () => {
    setIsPreLoading(false);
  };

  const handleFilterChange = (name, value) => {
    if (name === 'location') {
      setFilters((prev) => ({
        ...prev,
        location: value || '',
        neighborhood: value ? prev.neighborhood : ''
      }));
      return;
    }
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleNeighborhoodChange = (selectedOptions) => {
    if (!selectedOptions) {
      handleFilterChange('neighborhood', '');
      return;
    }
    const selectedValues = selectedOptions.map((option) => option.value).join(',');
    handleFilterChange('neighborhood', selectedValues);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
  };

  const handleClearFilters = () => {
    setFilters({ ...condoInitialFilters });
    setShowAdvancedFilters(false);
    setSelectedCondoId(null);
    setHoveredCondoId(null);
    navigate('/condominios', { replace: true });
    if (viewType === 'map') {
      window.location.reload();
    }
  };

  const toggleFavorite = async (condo) => {
    if (!condo?.id) return;
    try {
      const isFav = userFavorites.includes(condo.id);
      const db = getFirestore();

      if (!user) {
        const localFavorites = JSON.parse(localStorage.getItem('localCondominiumFavorites') || '[]');
        if (isFav) {
          const updated = localFavorites.filter((fid) => fid !== condo.id);
          localStorage.setItem('localCondominiumFavorites', JSON.stringify(updated));
          setUserFavorites(updated);
          showNotification('Condomínio removido dos favoritos', 'warning', 3000);
        } else {
          if (localFavorites.length >= 20) {
            showNotification('Limite de 20 favoritos atingido. Remova alguns para adicionar novos.', 'error', 5000);
            return;
          }
          const updated = [...localFavorites, condo.id];
          localStorage.setItem('localCondominiumFavorites', JSON.stringify(updated));
          setUserFavorites(updated);
          showNotification(
            'Condomínio adicionado aos favoritos. Faça login para salvar permanentemente.',
            'success',
            4000
          );
        }
        return;
      }

      if (isFav) {
        const favoriteSnapshot = await getDocs(
          query(
            collection(db, 'favorites'),
            where('userId', '==', user.uid),
            where('condominiumId', '==', condo.id)
          )
        );
        if (!favoriteSnapshot.empty) {
          await deleteDoc(favoriteSnapshot.docs[0].ref);
          setUserFavorites((prev) => prev.filter((fid) => fid !== condo.id));
          showNotification('Condomínio removido dos favoritos', 'warning', 3000);
        }
      } else {
        const allSnap = await getDocs(
          query(collection(db, 'favorites'), where('userId', '==', user.uid))
        );
        const condoCount = allSnap.docs.filter((d) => d.data().condominiumId).length;
        if (condoCount >= 20) {
          showNotification('Limite de 20 favoritos atingido. Remova alguns para adicionar novos.', 'error', 5000);
          return;
        }
        await setDoc(doc(collection(db, 'favorites')), {
          userId: user.uid,
          condominiumId: condo.id,
          createdAt: new Date()
        });
        setUserFavorites((prev) => [...prev, condo.id]);
        showNotification('Condomínio adicionado aos favoritos', 'success', 3000);
      }
    } catch (error) {
      console.error('Erro ao alternar favorito:', error);
      showNotification('Erro ao atualizar favoritos', 'error', 3000);
    }
  };

  const isFavorite = (id) => userFavorites.includes(id);

  const canViewCondominiumActions = useCallback(
    (condo) => {
      if (user?.isAdmin) {
        return { canEdit: true, canDelete: true, canDuplicate: true };
      }
      if (user && canAccessAdmin()) {
        const mine =
          condo.brokerId === user.uid ||
          condo.createdBy === user.uid ||
          condo.assignedTo === user.uid;
        if (mine) {
          return {
            canEdit: canEditProperties(),
            canDelete: canDeleteProperties(),
            canDuplicate: canCreateProperties()
          };
        }
      }
      return { canEdit: false, canDelete: false, canDuplicate: false };
    },
    [user, canAccessAdmin, canEditProperties, canDeleteProperties, canCreateProperties]
  );

  const getAdminCardProps = useCallback(
    (condo) => {
      const adminActions = canViewCondominiumActions(condo);
      return {
        adminActions,
        onEdit: adminActions.canEdit
          ? () => setQuickEditModal({ isOpen: true, condo })
          : undefined,
        onDuplicate: adminActions.canDuplicate
          ? () => navigate(`/duplicar-condominio/${condo.id}`)
          : undefined,
        onDelete: adminActions.canDelete
          ? () => setDeleteModal({ isOpen: true, condo })
          : undefined
      };
    },
    [canViewCondominiumActions, navigate]
  );

  const handleDelete = async () => {
    if (!deleteModal.condo) return;
    try {
      const db = getFirestore();
      if (user) {
        await logCondominiumDelete(user, deleteModal.condo);
      }
      await deleteDoc(doc(db, 'condominiums', deleteModal.condo.id));
      invalidateCondominiumsCatalog();
      setItems((prev) => prev.filter((c) => c.id !== deleteModal.condo.id));
      setDeleteModal({ isOpen: false, condo: null });
      showNotification('Condomínio excluído com sucesso', 'success', 3000);
    } catch (error) {
      console.error('Erro ao excluir condomínio:', error);
      showNotification('Erro ao excluir condomínio', 'error', 5000);
    }
  };

  const handleQuickEdit = async (updates) => {
    if (!quickEditModal.condo) return;
    try {
      const db = getFirestore();
      const mappedUpdates = { ...updates };

      if (updates.propertyStatus) {
        const status = updates.propertyStatus;
        if (status === 'Disponível') {
          mappedUpdates.isAvailable = true;
          mappedUpdates.isInactive = false;
          mappedUpdates.isReserved = false;
        } else {
          mappedUpdates.isAvailable = false;
          mappedUpdates.isInactive = status !== 'Reservado';
        }
        if (status === 'Reservado') {
          mappedUpdates.isReserved = true;
          mappedUpdates.displayTag = 'reserved';
        }
      }

      await setDoc(doc(db, 'condominiums', quickEditModal.condo.id), mappedUpdates, { merge: true });

      if (user) {
        const changeDiff = buildChangeDiff(quickEditModal.condo, { ...quickEditModal.condo, ...mappedUpdates });
        await logCondominiumUpdate(
          user,
          { ...quickEditModal.condo, ...mappedUpdates },
          { changeDiff }
        );
      }

      setItems((prev) =>
        prev.map((c) => (c.id === quickEditModal.condo.id ? { ...c, ...mappedUpdates } : c))
      );
      setQuickEditModal({ isOpen: false, condo: null });
      showNotification('Condomínio atualizado', 'success', 3000);
    } catch (error) {
      console.error('Erro ao atualizar condomínio:', error);
      showNotification('Erro ao atualizar condomínio', 'error', 5000);
    }
  };

  const handleMapShareModal = (modal) => {
    if (!modal) return;
    setShareModal({ isOpen: modal.isOpen, condo: modal.property || null });
  };

  const handleMapViewChange = (visibleProps) => {
    const ids = new Set(visibleProps.map((p) => p.id));
    setVisibleCondos(allFilteredCondos.filter((c) => ids.has(c.id)));
  };

  const getCondoUrl = (condo) => `${window.location.origin}/condominios/${condo.id}`;
  const getShareText = (condo) =>
    `Confira este condomínio: ${condo.title || 'Condomínio'} - ${condo.address || ''}`;

  const shareToWhatsApp = (condo) => {
    const url = getCondoUrl(condo);
    const text = getShareText(condo);
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank');
  };

  const shareToFacebook = (condo) => {
    const url = getCondoUrl(condo);
    const text = getShareText(condo);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareToLinkedIn = (condo) => {
    const url = getCondoUrl(condo);
    const text = getShareText(condo);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(condo.title || 'Condomínio')}&summary=${encodeURIComponent(text)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareToEmail = (condo) => {
    const url = getCondoUrl(condo);
    const text = getShareText(condo);
    const subject = `Condomínio: ${condo.title || 'Condomínio'}`;
    const body = `${text}\n\nVeja mais detalhes: ${url}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copyLink = async (condo) => {
    const url = getCondoUrl(condo);
    try {
      await navigator.clipboard.writeText(url);
      showNotification('Link copiado para a área de transferência!', 'success', 3000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showNotification('Link copiado para a área de transferência!', 'success', 3000);
    }
  };

  const orderSelectStyles = {
    ...customStyles,
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? 'var(--primary-color, #000000)' : '#cfd4dd',
      borderRadius: '0.5rem',
      minHeight: '40px',
      height: '40px',
      boxShadow: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      '&:hover': {
        borderColor: 'var(--primary-color, #000000)'
      },
      '&:focus, &:focus-within': {
        border: '2px solid var(--primary-color, #000000)',
        boxShadow: 'none'
      }
    }),
    singleValue: (base, { selectProps }) => ({
      ...base,
      color: selectProps.hasValue ? '#111827' : '#6B7280',
      fontSize: '14px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280',
      fontSize: '14px'
    })
  };

  const mapCommonProps = {
    properties: mapProperties,
    onPropertySelect: setSelectedCondoId,
    selectedPropertyId: selectedCondoId,
    hoveredPropertyId: hoveredCondoId,
    onMapViewChange: handleMapViewChange,
    detailBasePath: '/condominios',
    hideComparison: true,
    toggleFavorite,
    isFavorite,
    showNotification,
    setShareModal: handleMapShareModal
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <PreLoader isLoading={isPreLoading} onLoadingComplete={handlePreLoadingComplete} />

      <SEO
        title={companyName ? `${companyName} - Condomínios` : 'Condomínios'}
        description={
          companyName
            ? `Explore condomínios e empreendimentos com ${companyName}. Encontre o condomínio ideal para você.`
            : 'Explore nossa seleção de condomínios. Encontre o condomínio ideal para você.'
        }
        keywords="condomínios, empreendimentos, imóveis em condomínio, compra, venda"
        canonicalUrl="/condominios"
        pageType="website"
        includeSiteSchemas
      />
      <ScrollToTop />

      <div className="max-w-[85rem] mx-auto p-6 pt-24 w-full">
        <div className="mb-8">
          <BreadcrumbNav
            items={[
              { name: 'Início', path: '/' },
              { name: 'Condomínios', current: true },
            ]}
          />
        </div>

        <h1 className="text-3xl font-montserrat font-semibold mb-8 text-center">
          Encontre o condomínio ideal
        </h1>

        <Suspense
          fallback={
            <div className="space-y-4">
              <div className="h-[46px] rounded-lg bg-gray-100 animate-pulse" />
              <div className="grid grid-cols-2 gap-3">
                <SelectFallback />
                <SelectFallback />
              </div>
            </div>
          }
        >
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-3">
            <div className="relative lg:w-[35%]">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="text"
                placeholder="Nome, código, cidade ou bairro..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full border border-[#cfd4dd] rounded-lg pl-11 pr-4 h-[46px] text-[15px] focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-none lg:flex lg:w-[65%] gap-3 lg:gap-3">
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-none lg:flex gap-3 md:col-span-2 lg:w-[45%]">
                <div className="col-span-1 lg:w-1/2">
                  <Select
                    placeholder="Tipo"
                    value={typeOptions.find((option) => option.value === filters.type)}
                    onChange={(option) => handleFilterChange('type', option?.value || '')}
                    options={typeOptions}
                    isClearable={filters.type !== ''}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="col-span-1 lg:w-1/2">
                  <Select
                    placeholder="Finalidade"
                    value={purposeOptions.find((option) => option.value === filters.purpose)}
                    onChange={(option) => handleFilterChange('purpose', option?.value || '')}
                    options={purposeOptions}
                    isClearable={filters.purpose !== ''}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-none lg:flex gap-3 md:col-span-2 lg:w-[35%]">
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="w-full lg:w-2/3 px-4 h-[46px] rounded-lg text-[15px] font-medium transition-all duration-300 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                    color: 'var(--btn-outline-text, #3B82F6)',
                    border: '1px solid var(--btn-outline-border, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m0 12h9.75m-9.75 0a1.5 1.5 0 0 1 3 0m-3 0a1.5 1.5 0 0 0 3 0m-3.75 0H7.5m9-6h3.75M18 12a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H15" />
                  </svg>
                  {showAdvancedFilters ? 'Ocultar' : 'Avançado'}
                </button>

                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="w-full lg:w-1/2 px-4 h-[46px] rounded-lg text-[15px] font-medium transition-all duration-300 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                    color: 'var(--btn-outline-text, #3B82F6)',
                    border: '1px solid var(--btn-outline-border, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Limpar
                </button>
              </div>

              <div className="col-span-1 md:col-span-4 lg:w-[25%]">
                <button
                  type="submit"
                  className="w-full px-4 h-[46px] rounded-lg text-[15px] font-medium transition-colors duration-200 hover:opacity-90"
                  style={{
                    backgroundColor: 'var(--primary-color, #3B82F6)',
                    color: 'var(--secondary-color, #ffffff)'
                  }}
                >
                  Encontrar Condomínios
                </button>
              </div>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-[#cfd4dd]">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Cidade</label>
                  <Select
                    name="location"
                    value={cityOptions.find((option) => option.value === filters.location)}
                    onChange={(option) => handleFilterChange('location', option?.value || '')}
                    options={cityOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.location !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Bairro</label>
                  <Select
                    isMulti
                    name="neighborhood"
                    value={filters.neighborhood
                      .split(',')
                      .filter(Boolean)
                      .map((n) => ({ value: n, label: n }))}
                    onChange={handleNeighborhoodChange}
                    options={availableNeighborhoods.map((n) => ({ value: n, label: n }))}
                    styles={customStyles}
                    placeholder="Selecione os bairros"
                    isDisabled={!filters.location}
                    closeMenuOnSelect={false}
                    noOptionsMessage={() => 'Nenhuma opção disponível'}
                    isClearable
                    isSearchable
                    maxMenuHeight={200}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Preço/m²</label>
                  <Select
                    name="priceRange"
                    value={priceRangeOptions.find(
                      (option) => option.value === `${filters.minPrice}-${filters.maxPrice}`
                    )}
                    onChange={(option) => {
                      const [min, max] = (option?.value || '-').split('-');
                      setFilters((prev) => ({ ...prev, minPrice: min, maxPrice: max }));
                    }}
                    options={priceRangeOptions}
                    styles={customStyles}
                    placeholder="Qualquer valor"
                    isClearable={filters.minPrice !== '' || filters.maxPrice !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Área terreno</label>
                  <Select
                    name="areaRange"
                    value={areaRangeOptions.find(
                      (option) => option.value === `${filters.minArea}-${filters.maxArea}`
                    )}
                    onChange={(option) => {
                      const [min, max] = (option?.value || '-').split('-');
                      setFilters((prev) => ({ ...prev, minArea: min, maxArea: max }));
                    }}
                    options={areaRangeOptions}
                    styles={customStyles}
                    placeholder="Qualquer área"
                    isClearable={filters.minArea !== '' || filters.maxArea !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Unidades</label>
                  <Select
                    name="unitsRange"
                    value={unitsRangeOptions.find(
                      (option) => option.value === `${filters.minUnits}-${filters.maxUnits}`
                    )}
                    onChange={(option) => {
                      const [min, max] = (option?.value || '-').split('-');
                      setFilters((prev) => ({ ...prev, minUnits: min, maxUnits: max }));
                    }}
                    options={unitsRangeOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.minUnits !== '' || filters.maxUnits !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Status</label>
                  <Select
                    name="displayStatus"
                    value={displayStatusOptions.find((option) => option.value === filters.displayStatus)}
                    onChange={(option) => handleFilterChange('displayStatus', option?.value || '')}
                    options={displayStatusOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.displayStatus !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-row gap-2 sm:gap-4 items-center justify-between">
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <span className="text-gray-600 text-sm sm:text-base">
                {viewType === 'map' ? visibleCondos.length : totalCondos} condomínios encontrados
              </span>
              <div className="hidden sm:flex items-center gap-2 sm:ml-4">
                <button
                  type="button"
                  onClick={() => setViewType('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewType === 'grid' ? '' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  style={
                    viewType === 'grid'
                      ? {
                          color: 'var(--secondary-color, #ffffff)',
                          backgroundColor: 'var(--primary-color, #3B82F6)'
                        }
                      : {}
                  }
                  title="Visualização em grid"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setViewType('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewType === 'list' ? '' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  style={
                    viewType === 'list'
                      ? {
                          color: 'var(--secondary-color, #ffffff)',
                          backgroundColor: 'var(--primary-color, #3B82F6)'
                        }
                      : {}
                  }
                  title="Visualização em lista"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </button>
                {mapSettings.enabled && (
                  <button
                    type="button"
                    onClick={() => setViewType('map')}
                    className={`p-2 rounded-md transition-colors ${
                      viewType === 'map' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={
                      viewType === 'map'
                        ? {
                            color: 'var(--secondary-color, #ffffff)',
                            backgroundColor: 'var(--primary-color, #3B82F6)'
                          }
                        : {}
                    }
                    title="Visualização em mapa"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <div className="min-w-[120px] sm:min-w-[140px]">
                <Select
                  value={filters.orderBy ? orderByOptions.find((option) => option.value === filters.orderBy) : null}
                  onChange={(option) => handleFilterChange('orderBy', option?.value || '')}
                  options={orderByOptions}
                  styles={orderSelectStyles}
                  menuPortalTarget={document.body}
                  placeholder="Ordenar por"
                  isSearchable={false}
                />
              </div>
            </div>
          </div>
        </form>
        </Suspense>
      </div>

      {viewType === 'map' ? (
        <div className="flex-1 w-full relative flex flex-col md:flex-row lg:flex-row">
          <div
            className="h-[60vh] md:h-[calc(100dvh-300px)] lg:h-[calc(100dvh-300px)] relative z-0 flex-1 md:flex-1 lg:flex-1 min-h-[400px]"
            style={{ zIndex: 1 }}
          >
            {mapSettings.provider === 'mapbox' ? (
              <MapErrorBoundary>
                <Suspense fallback={<MapViewFallback />}>
                  <MapboxPropertyMap
                    key={mapKey}
                    {...mapCommonProps}
                    mapboxToken={mapSettings.mapboxToken}
                    mapboxStyle={mapSettings.mapboxStyle}
                  />
                </Suspense>
              </MapErrorBoundary>
            ) : (
              <MapErrorBoundary>
                <Suspense fallback={<MapViewFallback />}>
                  <PropertyMap key={mapKey} {...mapCommonProps} />
                </Suspense>
              </MapErrorBoundary>
            )}
          </div>

          <div
            className="h-[40vh] md:h-[calc(100dvh-300px)] lg:h-[calc(100dvh-300px)] bg-white shadow-2xl border-t md:border-t-0 lg:border-t-0 md:border-l lg:border-l border-gray-200 w-full md:w-1/2 lg:w-[650px] min-h-[300px]"
            style={{ zIndex: 1000 }}
          >
            <div className="overflow-y-auto h-full p-2">
              <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-2 relative z-10">
                {visibleCondos.map((condo) => (
                  <div
                    key={condo.id}
                    className={`relative z-10 ${
                      selectedCondoId === condo.id ? 'ring-2 rounded-lg' : ''
                    }`}
                    style={
                      selectedCondoId === condo.id
                        ? { '--tw-ring-color': 'var(--primary-color, #3B82F6)' }
                        : {}
                    }
                    onClick={() =>
                      setSelectedCondoId(selectedCondoId === condo.id ? null : condo.id)
                    }
                    onMouseEnter={() => setHoveredCondoId(condo.id)}
                    onMouseLeave={() => setHoveredCondoId(null)}
                  >
                    <CondominiumCard
                      condo={condo}
                      propertyCount={propertyCounts[condo.id] || 0}
                      isFavorite={isFavorite(condo.id)}
                      onToggleFavorite={toggleFavorite}
                      onShare={(c) => setShareModal({ isOpen: true, condo: c })}
                      viewType="grid"
                      className="min-h-0 shadow-sm hover:shadow-md"
                      {...getAdminCardProps(condo)}
                    />
                  </div>
                ))}
              </div>

              {visibleCondos.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p>Nenhum condomínio visível no mapa</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-[85rem] mx-auto p-6 pt-0 w-full">
          {loading ? (
            <PropertyGridSkeleton count={8} />
          ) : displayedCondos.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Nenhum condomínio encontrado com os filtros atuais.</p>
            </div>
          ) : (
            <div
              className={
                viewType === 'grid'
                  ? 'grid grid-cols-1 min-[560px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4'
                  : 'flex flex-col gap-4'
              }
            >
              {displayedCondos.map((condo) => (
                <CondominiumCard
                  key={condo.id}
                  condo={condo}
                  propertyCount={propertyCounts[condo.id] || 0}
                  viewType={viewType}
                  isFavorite={isFavorite(condo.id)}
                  onToggleFavorite={toggleFavorite}
                  onShare={(c) => setShareModal({ isOpen: true, condo: c })}
                  {...getAdminCardProps(condo)}
                />
              ))}
            </div>
          )}

          {hasMoreCondos && !loading && (
            <div ref={loadMoreRef} className="flex justify-center mt-8 mb-4">
              <div className="animate-pulse text-gray-400 text-sm">Carregando mais condomínios...</div>
            </div>
          )}

          {!hasMoreCondos && displayedCondos.length > 0 && (
            <div className="flex justify-center mt-8 mb-4">
              <p className="text-gray-500 text-sm">Você chegou ao final da lista</p>
            </div>
          )}
        </div>
      )}

      {deleteModal.isOpen && deleteModal.condo && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setDeleteModal({ isOpen: false, condo: null })}
          role="presentation"
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-xl font-montserrat font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o condomínio{' '}
              <span className="text-black font-medium">{deleteModal.condo.title}</span>?
            </p>
            <div className="flex justify-between gap-3">
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, condo: null })}
                className="px-4 py-2 rounded-lg transition-all duration-300"
                style={{
                  backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                  color: 'var(--btn-outline-text, #3B82F6)',
                  border: '1px solid var(--btn-outline-border, #3B82F6)'
                }}
              >
                Não
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
              >
                Sim
              </button>
            </div>
          </div>
        </div>
      )}

      {quickEditModal.isOpen && (
        <Suspense fallback={null}>
          <QuickEditModal
            isOpen={quickEditModal.isOpen}
            onClose={() => setQuickEditModal({ isOpen: false, condo: null })}
            property={quickEditModal.condo}
            onSave={handleQuickEdit}
            entityType="condominium"
            firestoreCollection="condominiums"
            onAdvancedEdit={() => {
              setQuickEditModal({ isOpen: false, condo: null });
              navigate(`/editar-condominio/${quickEditModal.condo.id}`);
            }}
          />
        </Suspense>
      )}

      {shareModal.isOpen && shareModal.condo && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShareModal({ isOpen: false, condo: null })}
          role="presentation"
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-condo-title"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 id="share-condo-title" className="text-xl font-semibold">
                Compartilhar Condomínio
              </h3>
              <button
                type="button"
                onClick={() => setShareModal({ isOpen: false, condo: null })}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>{shareModal.condo.title}</strong>
              </p>
              <p className="text-xs text-gray-500">{shareModal.condo.address}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  shareToWhatsApp(shareModal.condo);
                  setShareModal({ isOpen: false, condo: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-300 transition-colors"
              >
                <span className="text-sm font-medium">WhatsApp</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  shareToFacebook(shareModal.condo);
                  setShareModal({ isOpen: false, condo: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#1877F2]/10 hover:border-[#1877F2] transition-colors"
              >
                <span className="text-sm font-medium">Facebook</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  shareToLinkedIn(shareModal.condo);
                  setShareModal({ isOpen: false, condo: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#0A66C2]/10 hover:border-[#0A66C2] transition-colors"
              >
                <span className="text-sm font-medium">LinkedIn</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  shareToEmail(shareModal.condo);
                  setShareModal({ isOpen: false, condo: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <span className="text-sm font-medium">Email</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                copyLink(shareModal.condo);
                setShareModal({ isOpen: false, condo: null });
              }}
              className="w-full mt-4 flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <span className="text-sm font-medium">Copiar Link</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
